import { TrendingUp, TrendingDown, Clock, Euro, AlertTriangle, PackageCheck, type LucideIcon } from 'lucide-react';

export type InsightTone = 'pos' | 'neg' | 'warn' | 'info';

export interface ObraInsightConclusion {
  icon: LucideIcon;
  tone: InsightTone;
  text: string;
  financial?: boolean;
}

export interface ObraInsightInput {
  estado: string;
  horas_estimadas: number;
  horas_consumidas: number;
  coste_mano_obra_estimado: number;
  coste_consumido: number;
  precio_venta?: number | null;
  tiene_docs_entrega?: boolean;
}

const fmtEur = (n: number) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
const fmtPct = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(0)}%`;

export function computeObraInsights(obra: ObraInsightInput) {
  const finalizada = obra.estado === 'finalizada';
  const activa = obra.estado === 'activa';

  const desvHoras = obra.horas_consumidas - obra.horas_estimadas;
  const desvHorasPct = obra.horas_estimadas > 0 ? (desvHoras / obra.horas_estimadas) * 100 : 0;
  const desvCoste = obra.coste_consumido - obra.coste_mano_obra_estimado;
  const margen = obra.precio_venta != null ? obra.precio_venta - obra.coste_consumido : null;
  const margenPct =
    obra.precio_venta && obra.precio_venta > 0 && margen != null ? (margen / obra.precio_venta) * 100 : null;
  const consumoPct =
    obra.precio_venta && obra.precio_venta > 0 ? (obra.coste_consumido / obra.precio_venta) * 100 : null;

  const conclusiones: ObraInsightConclusion[] = [];

  if (finalizada) {
    if (margen != null) {
      if (margen < 0) {
        conclusiones.push({
          icon: TrendingDown,
          tone: 'neg',
          financial: true,
          text: `Pérdida de ${fmtEur(Math.abs(margen))}${margenPct != null ? ` (${margenPct.toFixed(0)}%)` : ''}.`,
        });
      } else {
        conclusiones.push({
          icon: TrendingUp,
          tone: 'pos',
          financial: true,
          text: `Margen positivo de ${fmtEur(margen)}${margenPct != null ? ` (${margenPct.toFixed(0)}%)` : ''}.`,
        });
      }
    } else {
      conclusiones.push({
        icon: AlertTriangle,
        tone: 'info',
        financial: true,
        text: 'Añade el precio de venta para calcular el margen.',
      });
    }

    if (obra.horas_estimadas > 0 && desvHoras > 0) {
      conclusiones.push({
        icon: Clock,
        tone: desvHorasPct > 15 ? 'neg' : 'warn',
        text: `+${desvHoras.toFixed(1)} h sobre lo previsto (${fmtPct(desvHorasPct)}).`,
      });
    } else if (obra.horas_estimadas > 0 && desvHoras < 0) {
      conclusiones.push({
        icon: Clock,
        tone: 'pos',
        text: `${desvHoras.toFixed(1)} h por debajo de lo previsto.`,
      });
    }

    if (obra.coste_mano_obra_estimado > 0 && desvCoste > 0) {
      conclusiones.push({
        icon: Euro,
        tone: 'neg',
        financial: true,
        text: `Coste real ${fmtEur(desvCoste)} por encima del presupuestado.`,
      });
    }

    if (!obra.tiene_docs_entrega) {
      conclusiones.push({
        icon: PackageCheck,
        tone: 'warn',
        text: 'Falta documentación de entrega.',
      });
    }
  } else if (activa && margen != null && consumoPct != null) {
    // Seguimiento en vivo cuando hay precio de venta
    if (consumoPct > 100) {
      conclusiones.push({
        icon: TrendingDown,
        tone: 'neg',
        financial: true,
        text: `Coste por encima de la venta: pérdida de ${fmtEur(Math.abs(margen))} (${consumoPct.toFixed(0)}% del precio).`,
      });
    } else if (consumoPct >= 90) {
      conclusiones.push({
        icon: AlertTriangle,
        tone: 'warn',
        financial: true,
        text: `Consumido el ${consumoPct.toFixed(0)}% de la venta; margen restante ${fmtEur(margen)}.`,
      });
    } else if (consumoPct >= 70) {
      conclusiones.push({
        icon: Euro,
        tone: 'warn',
        financial: true,
        text: `Consumido el ${consumoPct.toFixed(0)}% de la venta; margen actual ${fmtEur(margen)}${margenPct != null ? ` (${margenPct.toFixed(0)}%)` : ''}.`,
      });
    } else {
      conclusiones.push({
        icon: TrendingUp,
        tone: 'pos',
        financial: true,
        text: `Margen actual ${fmtEur(margen)}${margenPct != null ? ` (${margenPct.toFixed(0)}%)` : ''} — consumido ${consumoPct.toFixed(0)}%.`,
      });
    }
  }

  return { desvHoras, desvHorasPct, desvCoste, margen, margenPct, consumoPct, conclusiones };
}
